// NOTE: this link was helpful in the making of this component:
// https://redfin.engineering/how-to-fix-the-refresh-button-when-using-service-workers-a8e27af6df68

import React, { useEffect, useState, useContext } from "react";

const defaultServiceWorkerContext = {
  hasActiveSW: false,
  hasUpdate: false,
  isUpdating: false,
  handleUpdate: () => {}
};

export const ServiceWorkerContext = React.createContext(
  defaultServiceWorkerContext
);
export const ServiceWorkerConsumer = ServiceWorkerContext.Consumer;
export const useServiceWorker = () => useContext(ServiceWorkerContext);

// returns null or the current registration if browser supports service workers
const fetchCurrentRegistration = async (): Promise<
  ServiceWorkerRegistration | undefined
> => {
  if ("serviceWorker" in navigator) {
    return await navigator.serviceWorker.getRegistration();
  }
  return undefined;
};

export const ServiceWorkerProvider: React.FC = ({ children }) => {
  const [registration, setRegistration] = useState<
    ServiceWorkerRegistration | undefined
  >(undefined);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [handleUpdate, setHandleUpdate] = useState(() => () => {});

  useEffect(() => {
    fetchCurrentRegistration()
      .then(registration => {
        setRegistration(registration);
      })
      .catch(err => console.error(err));
  }, []);

  useEffect(() => {
    const handleSkipWaiting = (sw: ServiceWorker) => {
      sw.addEventListener("statechange", () => {
        let reloading; // make sure that we only reload the page once
        if (sw.state === "activated" && !reloading) {
          reloading = true;
          window.location.reload();
        }
      });
      sw.postMessage({ type: "SKIP_WAITING" });
      setIsUpdating(true);
    };

    const handleHasUpdate = (sw: ServiceWorker) => {
      setHasUpdate(true);
      setHandleUpdate(() => () => handleSkipWaiting(sw));
    };

    if (!registration) {
      // scenario #1: there's no active SW or the browser does not support SWs
      // manual testing: remove all service workers via devtools and refresh page
      return;
    } else if (registration.active) {
      if (registration.waiting) {
        // scenario #2: there's an active SW and a new one waiting
        // manual testing: reload page twice after a new service worker has been created/built
        handleHasUpdate(registration.waiting);
      } else {
        // scenario #3: there's an active SW and no waiting SW, but update may be found later
        // manual testing: reload page only once after a new service worker has been created
        registration.addEventListener("updatefound", () => {
          const installingWorker = registration.installing;
          if (installingWorker == null) return;
          installingWorker.addEventListener("statechange", () => {
            if (installingWorker.state === "installed") {
              handleHasUpdate(installingWorker);
            }
          });
        });
      }
    }
    // scenario #4: there's no currently active service worker, but one might get installed
    // in which case we don't want to show any UI
  }, [registration]);

  return (
    <ServiceWorkerContext.Provider
      value={{
        hasActiveSW: Boolean(registration?.active),
        hasUpdate,
        handleUpdate,
        isUpdating
      }}
    >
      {children}
    </ServiceWorkerContext.Provider>
  );
};
